import { withDropdownMenu } from "ui";
import styles from "./connect-existing-cases.scss";
import React, { useState } from "react";
import { DropdownMenuContext } from "ui/common/dropdown-menu/types";
import {
  dropdownRefContextMenu,
  isMobileScreen,
  isWindowLargeEnough
} from "app/components/helpers";
import { MdArrowDropDown, MdArrowLeft, MdArrowRight } from "react-icons/md";
import { CodeItemValueWire } from "app/components/options-handler/types";
import { ConnectCaseSelector } from "app/area/case/components/connected-cases/connect-case-selector";
import { withSearchCasesToConnect } from "app/area/case/features/search-cases/search-cases-to-connect";
import { FailedDomainConstraintMetaData, RenderMode } from "app/types-shared";
import { AddDocumentItemOptions, DeleteDocumentItemOptions } from "app/infrastructure/ui/form/types";
import { HeaderPanelProps } from "app/components/case-header-panel";
import { ConnectExistingWarningModal, DisconnectWarningModal } from "app/components/connect-existing-cases/connect-existing-warning-modal";

interface ConnectExistingCasesProps
  extends DropdownMenuContext, Partial<Omit<HeaderPanelProps, "label">> {
  caseId: number;
  options?: CodeItemValueWire[];
  label?: string;
  allowWarnings?: boolean;
  textWarningModalTitle?: string;
  textWarningModalDescription?: string;
  textWarningModalContinue?: string;
  textWarningModalEditCase?: string;
  textChangeStatusSuccess?: string;
  textWarningModalCancel?: string;  
  textDisconnectWarningModalTitle?: string;
  textDisconnectWarningModalDescription?: string;
  textDisconnectWarningModalContinue?: string;
  textDisconnectWarningModalCancel?: string;
  stateForCaseIds: { [caseId: string]: { valid: boolean; fetching: boolean } };
  createSearchForCasesToConnect: (draftId: number, connectionTypeId: number, renderMode?: RenderMode, domainId?: number, 
    caseType?: number) => Promise<number>;
  connectedCasesIdsByConnectionType: { [connectionTypeId: number]: number[] };
  onCloseCallback?: () => void;
  disconnectWarning?: boolean;
  subordinateId?: number;
  domainId?: number;
  caseConnectCaseType?: number;
}

export enum ConnectedCaseActionTypes {
  CONNECT = "CONNECT",
  DISCONNECT = "DISCONNECT"
}

const initialProps = {
  textAddExisting: '',
  textAddNew: '',
  textNoConnectionToItself: '',
  textConnect: '',
  textInvalidRangeError: '',
  textDisconnect: '',
  placeholderSearch: '',
  titleSearch: '',
  validateCaseId: null,
  favouriteComponent: null,
  maxSequence: 0,
  defaultOpen: true,
  hideButton: true,
  stateForCaseIds: {},
  filter: ''
};

const ConnectExistingCasesComponent = ({
  options,
  label,
  caseId,
  createSearchForCasesToConnect,
  connectedCasesIdsByConnectionType,
  onCloseCallback,
  disconnectWarning,
  stateForCaseIds,
  domainId,
  caseConnectCaseType,
  ...props
}: ConnectExistingCasesProps) => {
  const [state, setState] = useState({
    connectionType: null as CodeItemValueWire | null,
    connectCaseInfo: null as {
      connectedCaseId: number;
      type: ConnectedCaseActionTypes;
      warnings?: FailedDomainConstraintMetaData[];
      connectionTypeId: number;
    } | null,
    isRequestInProgress: false,
    isDisconnecting: false
  });

  const defaultOption  =
    !options?.length && props.subordinateId != null
      ? { v: props.subordinateId }
      : null;

  const { connectionType, connectCaseInfo, isRequestInProgress, isDisconnecting } = state;

  const setConnectionType = (option: CodeItemValueWire) =>
    setState((prev) => ({ ...prev, connectionType: option }));

  const resetConnectCaseInfo = () =>
    setState((prev) => ({
      ...prev,
      connectCaseInfo: null,
      isDisconnecting: false
  }));

  const onChangeCaseConnection = async ({ connectedCaseId, type, allowWarnings, connectionTypeId }: {
    connectedCaseId: number; type: ConnectedCaseActionTypes; allowWarnings: boolean; connectionTypeId?: number;
  }) => {
    if (isRequestInProgress) {
      return;
    }
    setState((prev) => ({ ...prev, isRequestInProgress: true, isDisconnecting: false }));
    try {
      const response = await props.onChangeCaseConnection({
        caseId,
        connectedCaseId,
        connectionTypeId: connectionType?.v || connectionTypeId,
        currentConnectedCases: connectedCasesIdsByConnectionType[connectionType.v] || [],
        allowWarnings,
        type
      });

      if (!allowWarnings && !!response?.warnings?.length) {
        setState((prev) => ({
          ...prev,
          connectCaseInfo: {
            connectedCaseId,
            type,
            warnings: response.warnings,
            connectionTypeId: connectionType?.v
          }
        }));
      } else {
        resetConnectCaseInfo();
      }
    } finally {
      setState((prev) => ({ ...prev, isRequestInProgress: false }));
    }
  };

  const handleDocumentItemChange = (type: ConnectedCaseActionTypes) =>
    (selectAddedItem: boolean, documentItemOptions: AddDocumentItemOptions | DeleteDocumentItemOptions) => {
      if (type === ConnectedCaseActionTypes.DISCONNECT && disconnectWarning) {
        setState((prev) => ({
          ...prev,
          isDisconnecting: true,
          connectCaseInfo: {
            connectedCaseId: documentItemOptions.childId,
            type,
            connectionTypeId: connectionType?.v
          }
        }));
      } else {
        onChangeCaseConnection({
          connectedCaseId: documentItemOptions.childId,
          type,
          allowWarnings: props.allowWarnings || false
        });
      }
    };
  const renderWarningModal = () => {
    if (connectCaseInfo?.warnings?.length && !props.allowWarnings) {
      return (
        <ConnectExistingWarningModal
          warnings={connectCaseInfo.warnings}
          title={props.textWarningModalTitle}
          description={props.textWarningModalDescription}
          continueText={props.textWarningModalContinue}
          editCaseText={props.textWarningModalEditCase}
          cancelText={props.textWarningModalCancel}
          footerActionOverride
          caseId={caseId}
          onContinue={() => {
            const { connectedCaseId, type } = connectCaseInfo;
            onChangeCaseConnection({
              connectedCaseId,
              type,
              allowWarnings: true,
              connectionTypeId: connectCaseInfo.connectionTypeId
            });
          }}
          onCancel={resetConnectCaseInfo}
          handleDropdownHide={props.handleDropdownHide}
        />
      );
    }

    if (isDisconnecting) {
      return (
        <DisconnectWarningModal
          title={props.textDisconnectWarningModalTitle}
          description={props.textDisconnectWarningModalDescription}
          continueText={props.textDisconnectWarningModalContinue}
          cancelText={props.textDisconnectWarningModalCancel}
          onContinue={() => {
            const { connectedCaseId, type } = connectCaseInfo;
            onChangeCaseConnection({
              connectedCaseId,
              type,
              allowWarnings: false,
              connectionTypeId: connectCaseInfo.connectionTypeId
            });
          }}
          onCancel={resetConnectCaseInfo}
          handleDropdownHide={props.handleDropdownHide}
        />
      );
    }
    return null;
  };

    const connectCaseSelector = React.useMemo(() => {
    return connectionType?.v ? (
      <ConnectCaseSelector
        {...initialProps}
        {...props}
        disableButton={isRequestInProgress}
        caseId={caseId}
        entityId={caseId}
        connectionTypeId={connectionType?.v}
        createSearchForCasesToConnect={
          () => createSearchForCasesToConnect(caseId, connectionType.v, RenderMode.read, domainId, caseConnectCaseType)
        }
        connectedCasesIds={connectedCasesIdsByConnectionType?.[connectionType.v] || []}
        onCloseCallback={() => {
          setConnectionType(null);
          if (onCloseCallback) {
            onCloseCallback();
          }
        }}
        onAddDocumentItem={handleDocumentItemChange(ConnectedCaseActionTypes.CONNECT)}
        onDeleteDocumentItem={handleDocumentItemChange(ConnectedCaseActionTypes.DISCONNECT)}
        stateForCaseIds={stateForCaseIds}
      />
    ) : null;
  }, [connectionType, caseId, connectedCasesIdsByConnectionType, isRequestInProgress, stateForCaseIds]);

  if (!label) {
    return <></>;
  }
  return (
    <>
      <li className={styles.nestedDropdown}>
        <div 
          className="dropdown-item" 
          tabIndex={0}
          onClick={!options?.length && defaultOption ? () => setConnectionType(defaultOption) : undefined}
        >
          <span>{label}</span>
          {options?.length ? <div className={styles.icon}><ArrowIcon /></div> : null}
        </div>
        {options?.length ? <DropdownOptions options={options} onClick={setConnectionType} /> : null}
      </li>
      {connectCaseSelector}
      {renderWarningModal()}
    </>
  );
};

export const ConnectExistingCases = withSearchCasesToConnect(
  withDropdownMenu(ConnectExistingCasesComponent), null
);


const ArrowIcon = () => {
  if (isMobileScreen()) {
    return (
      <MdArrowDropDown className={`dropdown-arrow ${styles.navItemIcon}`} />
    );
  }
  return isWindowLargeEnough() ? (
    <MdArrowRight className={`dropdown-arrow ${styles.navItemIcon}`} />
  ) : (
    <MdArrowLeft className={`dropdown-arrow ${styles.navItemIcon}`} />
  );
};

const DropdownOptions = ({
  options,
  onClick
}: {
  options?: CodeItemValueWire[];
  onClick: (option: CodeItemValueWire) => void;
}) => {
  return (
    <ul
      className={`dropdown-menu ${styles.subMenu}`}
      ref={dropdownRefContextMenu}
    >
      {options?.map((option: CodeItemValueWire, index: number) => (
        <li key={index}>
          <button
            className="dropdown-item"
            type="button"
            onClick={() => onClick(option)}
          >
            {option.d}
          </button>
        </li>
      ))}
    </ul>
  );
};
